/**
 * Alsunu — add-to-cart pending state.
 *
 * Horizon plays its checkmark burst optimistically on click, which looks great
 * but tells you nothing while the network request is actually in flight. On a
 * phone connection that gap is where shoppers tap twice. This marks the button
 * with data-als-pending for the duration of the request; the CSS in
 * als-page-styles.css §16 turns that into a subtle sheen.
 *
 * One delegated listener, no per-button wiring, no layout reads.
 */
(() => {
  const PENDING = 'data-als-pending';
  const SAFETY_MS = 8000;

  /** @type {Map<HTMLElement, number>} */
  const timers = new Map();

  const clear = (button) => {
    if (!button) return;
    button.removeAttribute(PENDING);
    const id = timers.get(button);
    if (id) clearTimeout(id);
    timers.delete(button);
  };

  const clearAll = () => {
    document.querySelectorAll(`[${PENDING}]`).forEach(clear);
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('button[name="add"]');
      if (!button || button.disabled || button.hasAttribute(PENDING)) return;

      // Don't show progress for a form the browser is about to reject.
      const form = button.closest('form');
      if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return;

      button.setAttribute(PENDING, 'true');
      timers.set(button, window.setTimeout(() => clear(button), SAFETY_MS));
    },
    { passive: true, capture: true }
  );

  ['shopify:cart:lines-update', 'shopify:cart:error'].forEach((name) => {
    document.addEventListener(name, clearAll, { passive: true });
  });

  // Belt and braces: a cart drawer opening means the round trip finished.
  document.addEventListener('dialog:open', clearAll, { passive: true });
  window.addEventListener('pageshow', clearAll, { passive: true });

  /**
   * Quick add renders the whole product template inside a ~615px modal, and
   * als-quick-add.css hides the page-depth blocks there. That leaves no way
   * to reach the full product page from the modal — Horizon ships no such
   * link — so anyone who opened it to evaluate rather than to buy is stuck
   * closing it and hunting for the card again. Append the link ourselves; the
   * product URL is already in the modal on the title.
   *
   * Content arrives after the dialog opens and is replaced wholesale when a
   * different product is chosen, so this observes rather than runs once.
   */
  const watchQuickAddModal = () => {
    const modal = document.querySelector('.quick-add-modal');
    if (!modal || modal.dataset.alsDetailsWatched) return;
    modal.dataset.alsDetailsWatched = 'true';

    const inject = () => {
      if (modal.querySelector('.als-qa-details')) return;
      const href = modal.querySelector('a[href*="/products/"]')?.getAttribute('href');
      // Desktop and mobile get different quick-add templates: one wraps the
      // buttons in .buy-buttons-block, the other in .product-form-buttons.
      // Anchor off the button itself so both land in the same place.
      const addButton = modal.querySelector('button[name="add"]');
      const container =
        addButton?.closest('[class*="buy-buttons"], [class*="product-form-buttons"]') ?? addButton?.closest('form');
      if (!href || !container) return;

      const link = document.createElement('a');
      link.className = 'als-qa-details';
      link.href = href;
      link.textContent = 'View full details';
      container.append(link);
    };

    new MutationObserver(inject).observe(modal, { childList: true, subtree: true });
    inject();
  };

  /**
   * Horizon's quick add reads the product URL from its enclosing
   * `product-card`. The compact als-rail-card is not one, so "Choose" opened
   * an empty modal. Give those instances the URL the card already knows,
   * before Horizon's own click handler runs (this listener captures first).
   */
  const bindRailQuickAdd = (component) => {
    if (!component || Object.prototype.hasOwnProperty.call(component, 'productPageUrl')) return;
    const url = component.closest('[data-rail-add]')?.dataset.productUrl;
    if (!url) return;
    Object.defineProperty(component, 'productPageUrl', {
      configurable: true,
      get: () => new URL(url, window.location.origin).href,
    });
  };

  // Horizon's declarative click handler is registered before this deferred
  // file, so binding inside our own click listener is too late. Bind up front,
  // for cards added later (shop rail panels are swapped in), and as a pointer
  // or keyboard approaches a card.
  const bindAll = (root) => root.querySelectorAll?.('[data-rail-add] quick-add-component').forEach(bindRailQuickAdd);
  bindAll(document);
  new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach((node) => node instanceof Element && bindAll(node));
  }).observe(document.body, { childList: true, subtree: true });
  ['pointerdown', 'focusin'].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        bindRailQuickAdd(target?.closest('[data-rail-add] quick-add-component'));
      },
      { passive: true, capture: true }
    )
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const component = target?.closest('quick-add-component');
      if (!component) return;
      bindRailQuickAdd(component);
      requestAnimationFrame(watchQuickAddModal);
    },
    { passive: true, capture: true }
  );
})();
